import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { RootState, StoreDispatch } from "../../store/configure-store"
import { useQuery as reactUseQuery } from "@tanstack/react-query"
import { FromDropdown } from "../../cmps/forms/input/form-dropdown"
import { SystemTypes, isRequiredSystem } from '../../cmps/forms/is-required-system'
import { UserMsg } from "../../models/user"
import { setUserMsg } from "../../store/user"
import { AddEntityModal, EntityDetails } from "../../cmps/forms/modals/add-entity-modal"
import { AddEntitySystemModal, EntitySystemDetails } from "../../cmps/forms/modals/add-entity-system-modal"
import { AddAuditYearModal, AuditYearDetails } from "../../cmps/forms/modals/add-audit-year-modal"
import { httpService } from "../../services/http-service"
import { createEntitySystemMutation } from "../../cmps/util-cmps/graphql-mutations/create-entity-system"
import { createAuditMutation } from "../../cmps/util-cmps/graphql-mutations/create-audit"
import { createEntityMutation } from "../../cmps/util-cmps/graphql-mutations/create-entity"
import { getControlByObjectiveAndSystemsQuery } from "../../cmps/util-cmps/graphql-queries/control-by-objective-and-systems"
import { ExistingControl, ExistingControlSection } from "../../cmps/forms/existing-control-section"
import { createAuditControlMutation } from "../../cmps/util-cmps/graphql-mutations/create-audit-control"
import { Spinner } from "../../cmps/util-cmps/spinner"
import { Backlog } from "../../cmps/util-cmps/backlog"
import { useEscapeKey } from "../../hooks/use-escape-key"
import { ConsolidationConfigBuilder, validateConsolidationPairings } from "../../cmps/audit-control/consolidation-config-builder"

interface PageDetailsRes {
    companies: {
        id: number
        name: string
        entity: {
            id: number
            name: string
            entitySystems: {
                id: number
                system: {
                    id: number
                    name: string
                }
            }[]
            audit: {
                id: number
                year: number
            }[]
        }[]
        user: {
            id: string
            email: string
            firstName: string
            lastName: string
        }[]
    }[]
    objectives: {
        id: number
        name: string
        objectiveNumber: number
        domainAbbreviation: string
    }[]
}

interface SelectedDetails {
    entityId: number | null
    objectiveId: number | null
    mainSystemId: number | null
    hrSystemId: number | null
    ticketSystemId: number | null
    overwriteControlId: number | null
    consolidationJson: string | null
    auditId: number | null
    controlOwnerId: string | null
}

const pageDetailsQuery = (companyName: string) => `
{
  companies(name: "${companyName}") {
    id
    name
    entity(sorting: ASC) {
      id
      name
      entitySystems(sorting: ASC) {
        id
        system {
          id
          name
        }
      }
      audit(sorting: DESC) {
        id
        year
        auditor {
            id
            name
        }
      }
    }
    user {
      id
      email
      firstName
      lastName
    }
  }
  objectives(sorting: ASC) {
    id
    name
    objectiveNumber
    domainAbbreviation
  }
}
`

const getEmptySelectedDetails = () => ({
    entityId: null,
    objectiveId: null,
    mainSystemId: null,
    hrSystemId: null,
    ticketSystemId: null,
    overwriteControlId: null,
    consolidationJson: null,
    auditId: null,
    controlOwnerId: null,
})

interface AddAuditControlFormPageProps {
    onClose: () => void
    onSuccess?: () => void
}

export const AddAuditControlFormPage: React.FC<AddAuditControlFormPageProps> = ({ onClose, onSuccess }) => {
    const dispatch = useDispatch<StoreDispatch>()
    const companyName: any = useSelector((stateModule: RootState) => stateModule.companyName)!
    const [pageDetails, setPageDetails] = useState<PageDetailsRes>()
    const [selectedDetails, setSelectedDetails] = useState<SelectedDetails>(getEmptySelectedDetails())
    const [existingControl, setExistingControl] = useState<ExistingControl>({ status: "new", controlId: null })
    const [isAddEntityModalOpen, setIsAddEntityModalOpen] = useState(false)
    const [openedEntitySystemModal, setOpenedEntitySystemModal] = useState<SystemTypes | null>(null)
    const [isAddAuditYearModalOpen, setIsAddAuditYearModalOpen] = useState(false)
    const [isAddingAuditControl, setIsAddingAuditControl] = useState(false)

    const company = pageDetails?.companies?.[0] ?? null

    useEscapeKey(onClose)

    const fetchPageDetails = async () => {
        const query = pageDetailsQuery(companyName.value)
        const pageDetails: PageDetailsRes = await httpService.graphql(companyName.value, query)

        setPageDetails(pageDetails)
        return pageDetails
    }

    const { isLoading, isFetching, isError, refetch: refetchPageDetails } = reactUseQuery({
        queryKey: ['page-details'],
        queryFn: fetchPageDetails
    })

    const fetchExistingControl = async () => {
        setExistingControl({ status: "loading", controlId: null })

        try {
            const query = getControlByObjectiveAndSystemsQuery()
            const variables = {
                objectiveId: selectedDetails.objectiveId,
                mainSystemId: selectedDetails.mainSystemId,
                hrSystemId: selectedDetails.hrSystemId,
                ticketSystemId: selectedDetails.ticketSystemId
            }
            const reqBody = { query, variables }
            const res = await httpService.graphql(companyName.value, reqBody)

            if (res.controlByObjectiveAndSystems === null) {
                setExistingControl({ status: "fetched", controlId: null })
                return
            }

            const controlId = res.controlByObjectiveAndSystems.id
            const consolidationJson = res.controlByObjectiveAndSystems.systemConsolidations
            setExistingControl({ status: "fetched", controlId, consolidationJson: consolidationJson ? JSON.stringify(consolidationJson) : null })
        } catch {
            const msg: UserMsg = { type: 'danger', txt: 'Failed checking whether control exists' }
            dispatch(setUserMsg({ msg }))
        }
    }

    useEffect(() => {
        setExistingControl({ status: "new", controlId: null })
        setSelectedDetails(prevState => ({ ...prevState, overwriteControlId: null }))

        const { entityId, objectiveId, mainSystemId, hrSystemId } = selectedDetails
        if (!entityId || !objectiveId ||
            (isRequiredSystem(getObjectiveNum(), SystemTypes.IN_SCOPE) && !mainSystemId) ||
            (isRequiredSystem(getObjectiveNum(), SystemTypes.HR) && !hrSystemId)
        ) {
            return
        }

        fetchExistingControl()

    }, [selectedDetails.entityId, selectedDetails.objectiveId, selectedDetails.mainSystemId, selectedDetails.hrSystemId, selectedDetails.ticketSystemId])

    useEffect(() => {
        setSelectedDetails(getEmptySelectedDetails())
        refetchPageDetails()
    }, [companyName.value])

    const onChangeFormValue = (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
        const { name, value } = event.target

        let updatedValue: null | string | number = value
        if (value === '') updatedValue = null
        else if (!['consolidationJson', 'controlOwner', 'itSupport'].includes(name)) updatedValue = +value

        if (name === 'entity') {
            const updatedSelectedDetails = getEmptySelectedDetails()

            setSelectedDetails({
                ...updatedSelectedDetails,
                entityId: updatedValue as number,
                objectiveId: getObjectiveNum(),
                controlOwnerId: selectedDetails.controlOwnerId
            })
            return
        }

        const key = name === 'consolidationJson' ? name : `${name}Id`
        setSelectedDetails({ ...selectedDetails, [key]: updatedValue })
    }

    const onExistingControlOverride = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedOption = event.target.id;
        if (selectedOption === 'override-yes') {
            setSelectedDetails({
                ...selectedDetails,
                overwriteControlId: existingControl.controlId,
                consolidationJson: existingControl.consolidationJson || selectedDetails.consolidationJson,
            })
            return
        }
        setSelectedDetails({ ...selectedDetails, overwriteControlId: 0 })
    }

    const getSelectedEntity = () => {
        const selectedEntityId = selectedDetails.entityId
        if (!selectedEntityId || !company) return null

        return company.entity.find((entity) => entity.id === selectedEntityId)
    }

    const getEntitySystemList = () => {
        const selectedEntity = getSelectedEntity()
        if (!selectedEntity) return []

        return selectedEntity.entitySystems.map(
            (entitySystem) => ({ id: entitySystem.id, name: entitySystem.system.name })
        )
    }

    const getObjectiveNum = () => {
        const selectedObjectiveId = selectedDetails.objectiveId
        if (!selectedObjectiveId) return null

        return pageDetails!.objectives.find((objective) => objective.id === selectedObjectiveId)?.objectiveNumber ?? null
    }

    /** Returns system types for consolidation builder based on actually selected systems */
    const getSelectedSystemTypes = () => {
        const types: string[] = []
        if (selectedDetails.mainSystemId) types.push(SystemTypes.IN_SCOPE)
        if (selectedDetails.hrSystemId) types.push(SystemTypes.HR)
        if (selectedDetails.ticketSystemId) types.push(SystemTypes.TICKET)
        return types.length > 0 ? types : [SystemTypes.IN_SCOPE]
    }

    const onConsolidationChange = (val: string) => {
        setSelectedDetails(prev => ({ ...prev, consolidationJson: val }))
    }

    const getAuditYearList = () => {
        const selectedEntity = getSelectedEntity()
        if (!selectedEntity) return []

        return selectedEntity.audit.map(
            (audit) => ({ id: audit.id, name: audit.year.toString() })
        )
    }

    const getUsersList = () => {
        if (!company) return []
        return company.user.map((user) => ({ id: user.id, name: `${user.firstName} ${user.lastName}` }))
    }

    const onAddEntity = async (entityDetails: EntityDetails) => {
        if (!company) return
        const { name, abbreviation, bucketName, domains } = entityDetails
        const query = createEntityMutation(company.id, name, abbreviation, bucketName, domains)
        const res = await httpService.graphql(companyName.value, query)
        const newEntity = { ...res.createEntity.entity, entitySystems: [], audit: [] }

        const updatedEntities = [...company.entity, newEntity].sort((a, b) => a.name.localeCompare(b.name))
        const updatedCompany = { ...company, entity: updatedEntities }

        const updatedPageDetails = {
            ...pageDetails,
            companies: [updatedCompany]
        }
        setPageDetails(updatedPageDetails as PageDetailsRes)
        setSelectedDetails({ ...selectedDetails, entityId: res.createEntity.entity.id })
        setIsAddEntityModalOpen(false)
    }

    const onAddEntitySystem = async (systemType: SystemTypes, entitySystemDetails: EntitySystemDetails) => {
        if (!company) return
        const { systemId, integrationType, isRequiredDaily } = entitySystemDetails
        const query = createEntitySystemMutation(selectedDetails.entityId!, systemId!, integrationType, isRequiredDaily)
        const res = await httpService.graphql(companyName.value, query)
        const newEntitySystem = res.createEntitySystem.entitySystem

        const selectedEntity = getSelectedEntity()
        const updatedEntitySystems = [...selectedEntity!.entitySystems, newEntitySystem].sort((a, b) => a.system.name.localeCompare(b.system.name))
        const updatedEntity = { ...selectedEntity, entitySystems: updatedEntitySystems }
        const updatedEntities = company.entity.map((entity) => entity.id === selectedDetails.entityId ? updatedEntity : entity)
        const updatedCompany = { ...company, entity: updatedEntities }

        const updatedPageDetails = {
            ...pageDetails,
            companies: [updatedCompany]
        }
        setPageDetails(updatedPageDetails as PageDetailsRes)
        setSelectedDetails({ ...selectedDetails, [`${systemType}SystemId`]: newEntitySystem.id })
        setOpenedEntitySystemModal(null)
    }

    const onAddAuditYear = async (auditYearDetails: AuditYearDetails) => {
        if (!company) return
        const { year, auditorId } = auditYearDetails
        const query = createAuditMutation(selectedDetails.entityId!, year, auditorId!)
        const res = await httpService.graphql(companyName.value, query)
        const { auditor, ...newAudit } = res.createAudit.audit

        const selectedEntity = getSelectedEntity()
        const updatedAudit = [...selectedEntity!.audit, newAudit].sort((a, b) => b.year - a.year)
        const updatedEntity = { ...selectedEntity, audit: updatedAudit }
        const updatedEntities = company.entity.map((entity) => entity.id === selectedDetails.entityId ? updatedEntity : entity)
        const updatedCompany = { ...company, entity: updatedEntities }

        const updatedPageDetails = {
            ...pageDetails,
            companies: [updatedCompany]
        }
        setPageDetails(updatedPageDetails as PageDetailsRes)
        setSelectedDetails({ ...selectedDetails, auditId: newAudit.id })
        setIsAddAuditYearModalOpen(false)
    }

    const onAddAuditControl = async () => {
        if (existingControl.status === 'loading') return

        const { entityId, objectiveId, mainSystemId, hrSystemId, ticketSystemId, consolidationJson, auditId: auditYearId, controlOwnerId } = selectedDetails

        if (!entityId) return triggerErrorMsg('Entity is required')
        if (!objectiveId) return triggerErrorMsg('Objective is required')
        if (isRequiredSystem(getObjectiveNum(), SystemTypes.IN_SCOPE) && !mainSystemId) return triggerErrorMsg('Main System is required')
        if (isRequiredSystem(getObjectiveNum(), SystemTypes.HR) && !hrSystemId) return triggerErrorMsg('HR System is required')
        // Ticket system is never blocking — missing it just makes the AC "partial"
        const hasHrSelected = !!hrSystemId
        const hasTicketSelected = !!ticketSystemId
        const hasMultipleSystems = hasHrSelected || hasTicketSelected

        if (hasMultipleSystems) {
            if (!consolidationJson || consolidationJson === '[]') return triggerErrorMsg('Consolidation config is required')
            const pairingError = validateConsolidationPairings(consolidationJson, hasHrSelected, hasTicketSelected)
            if (pairingError) {
                const msg: UserMsg = { type: 'danger', txt: pairingError }
                dispatch(setUserMsg({ msg }))
                return
            }
        }

        if (!auditYearId) return triggerErrorMsg('Audit Year is required')
        if (!controlOwnerId) return triggerErrorMsg('Control Owner is required')
        if (existingControl.status === 'fetched' && existingControl.controlId && selectedDetails.overwriteControlId === null) {
            return triggerErrorMsg('Please select whether to overwrite or create new control')
        }

        setIsAddingAuditControl(true)
        try {
            const query = createAuditControlMutation()
            const variables = {
                companyId: company!.id,
                ...selectedDetails,
            }
            const reqBody = { query, variables }
            const res = await httpService.graphql(companyName.value, reqBody)

            const auditControl = res.createAuditControl.auditControl
            setExistingControl({ status: "fetched", controlId: auditControl.control.id })
            const msg: UserMsg = { type: 'success', txt: `Audit control created successfully (${auditControl.id})` }
            dispatch(setUserMsg({ msg }))
            onSuccess?.()
        } catch {
            const msg: UserMsg = { type: 'danger', txt: 'Failed creating audit control' }
            dispatch(setUserMsg({ msg }))
        }
        finally {
            setIsAddingAuditControl(false)
        }

    }

    const triggerMissingRequiredFields = () => {
        triggerErrorMsg('Missing Required Fields')
    }

    const triggerErrorMsg = (txt: string) => {
        const msg: UserMsg = { type: 'danger', txt }
        dispatch(setUserMsg({ msg }))
    }

    const renderBody = () => {
        if (isError) return <div className="modal-form-body"><p>Error loading form data.</p></div>
        if (isLoading || isFetching) return <div className="modal-form-body"><Spinner isSmall={true} /></div>
        if (!company) return <div className="modal-form-body"><p>No company data found.</p></div>

        return <section className="modal-form-body">
            <div className="form-section-card">
                <div className="form-section-label">Entity & Objective</div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName={'entity'}
                        label="Entity"
                        options={company.entity}
                        value={selectedDetails.entityId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={() => setIsAddEntityModalOpen(true)}
                    />
                </div>
                <div className="field">
                    <FromDropdown
                        keyName={'objective'}
                        label="Objective"
                        options={pageDetails!.objectives}
                        value={selectedDetails.objectiveId || ''}
                        onSelectValue={onChangeFormValue}
                    />
                </div>
            </div>

            <div className="form-section-card">
                <div className="form-section-label">Systems</div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName={'mainSystem'}
                        label="Main System"
                        isRequired={isRequiredSystem(getObjectiveNum(), SystemTypes.IN_SCOPE)}
                        options={getEntitySystemList()}
                        value={selectedDetails.mainSystemId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={getSelectedEntity() ? (() => setOpenedEntitySystemModal(SystemTypes.IN_SCOPE)) : null}
                    />
                </div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName={'hrSystem'}
                        label="HR System"
                        isRequired={isRequiredSystem(getObjectiveNum(), SystemTypes.HR)}
                        options={getEntitySystemList()}
                        value={selectedDetails.hrSystemId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={getSelectedEntity() ? (() => setOpenedEntitySystemModal(SystemTypes.HR)) : null}
                    />
                </div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName={'ticketSystem'}
                        label="Ticket System"
                        isRequired={isRequiredSystem(getObjectiveNum(), SystemTypes.TICKET)}
                        options={getEntitySystemList()}
                        value={selectedDetails.ticketSystemId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={getSelectedEntity() ? (() => setOpenedEntitySystemModal(SystemTypes.TICKET)) : null}
                    />
                </div>
                <ExistingControlSection
                    existingControl={existingControl}
                    onExistingControlOverride={onExistingControlOverride}
                />
                {(() => {
                    const objNum = getObjectiveNum()
                    const missing: string[] = []
                    if (isRequiredSystem(objNum, SystemTypes.IN_SCOPE) && !selectedDetails.mainSystemId) missing.push('Main System')
                    if (isRequiredSystem(objNum, SystemTypes.HR) && !selectedDetails.hrSystemId) missing.push('HR System')
                    if (isRequiredSystem(objNum, SystemTypes.TICKET) && !selectedDetails.ticketSystemId) missing.push('Ticket System')
                    if (missing.length === 0 || !objNum) return null
                    return (
                        <div className="partial-warning">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>Partial setup — missing required system{missing.length > 1 ? 's' : ''}: <strong>{missing.join(', ')}</strong></span>
                        </div>
                    )
                })()}
            </div>

            <div className="form-section-card">
                <div className="form-section-label">Consolidation Config</div>
                <ConsolidationConfigBuilder
                    value={selectedDetails.consolidationJson || '[]'}
                    onChange={onConsolidationChange}
                    availableSystemTypes={getSelectedSystemTypes()}
                />
            </div>

            <div className="form-section-card">
                <div className="form-section-label">Audit</div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName={'audit'}
                        label="Audit Year"
                        options={getAuditYearList()}
                        value={selectedDetails.auditId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={getSelectedEntity() ? (() => setIsAddAuditYearModalOpen(true)) : null}
                    />
                </div>
                <div className="field">
                    <FromDropdown
                        keyName={'controlOwner'}
                        label="Control Owner"
                        options={getUsersList()}
                        value={selectedDetails.controlOwnerId || ''}
                        onSelectValue={onChangeFormValue}
                    />
                </div>
            </div>

            <p className="form-info">New audit controls are created as active by default. You can change this later.</p>

            <div className="form-actions">
                {!isAddingAuditControl && <button
                    className="main"
                    onClick={onAddAuditControl}
                    disabled={(existingControl.status !== 'fetched') || isAddingAuditControl}>
                    Create
                </button>}
                {isAddingAuditControl && <span className="spinner"><Spinner isSmall={true} /></span>}
            </div>
        </section>
    }

    return <>
        <section className="audit-control-form-modal">
            <header>
                <h2>Create Audit Control</h2>
                <button className="close-btn" onClick={onClose}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            {renderBody()}
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />

        {isAddEntityModalOpen && <AddEntityModal
            onAddEntity={onAddEntity}
            onCloseModal={() => setIsAddEntityModalOpen(false)}
            triggerMissingRequiredFields={triggerMissingRequiredFields}
            triggerErrorMsg={triggerErrorMsg}
        />}

        {openedEntitySystemModal && <AddEntitySystemModal
            entityName={(getSelectedEntity() || {}).name || ''}
            systemType={openedEntitySystemModal}
            onAddEntitySystem={onAddEntitySystem}
            onCloseModal={() => setOpenedEntitySystemModal(null)}
            triggerMissingRequiredFields={triggerMissingRequiredFields}
            triggerErrorMsg={triggerErrorMsg}
        />}

        {isAddAuditYearModalOpen && <AddAuditYearModal
            entityName={(getSelectedEntity() || {}).name || ''}
            onAddAuditYear={onAddAuditYear}
            onCloseModal={() => setIsAddAuditYearModalOpen(false)}
            triggerMissingRequiredFields={triggerMissingRequiredFields}
            triggerErrorMsg={triggerErrorMsg}
        />}
    </>
}
